import { EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, Drawer, Space, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { listResource } from "../../api/resourceApi";
import { PageHeaderActions } from "../../shared/components/PageHeaderActions";
import { PersistedTable } from "../../shared/components/PersistedTable";
import { useEntityFormLauncher } from "../../shared/entityForms/EntityFormLauncherHost";

type BankRow = { id: number; name: string; ifsc?: string; accountCount?: number; activeAccountCount?: number };
type BankAccountRow = {
  id: number;
  accountName: string;
  accountNumber: string;
  bankName?: string;
  openingBalance?: number;
  isActive?: boolean;
  facilityType?: "Normal" | "Overdraft" | "CashCredit";
  overdraftLimit?: number | null;
};

export function BankPage() {
  const openForm = useEntityFormLauncher();
  const [selectedBank, setSelectedBank] = useState<BankRow | null>(null);
  const banks = useQuery({ queryKey: ["banks"], queryFn: () => listResource<BankRow>("/banks") });

  const columns: ColumnsType<BankRow> = [
    { title: "Bank", dataIndex: "name" },
    { title: "IFSC", dataIndex: "ifsc", width: 160 },
    { title: "Accounts", dataIndex: "accountCount", width: 120 },
    { title: "Active", dataIndex: "activeAccountCount", width: 120 },
    {
      title: "",
      width: 160,
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" onClick={() => setSelectedBank(row)}>Accounts</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openForm({ module: "bank", mode: "edit", id: row.id, source: "bank-page", onSuccess: () => banks.refetch() })} />
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageHeaderActions
        title="Bank"
        subtitle="Bank master with inline bank-account management."
        right={
          <>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm({ module: "bank", mode: "create", source: "bank-page", onSuccess: () => banks.refetch() })}>Create Bank</Button>
            <Button icon={<ReloadOutlined />} onClick={() => banks.refetch()} />
          </>
        }
      />
      <PersistedTable tableId="banks" rowKey="id" size="small" bordered loading={banks.isLoading} dataSource={banks.data ?? []} columns={columns} />
      <BankAccountsDrawer bank={selectedBank} onClose={() => setSelectedBank(null)} />
    </div>
  );
}

function BankAccountsDrawer({ bank, onClose }: { bank: BankRow | null; onClose: () => void }) {
  const openForm = useEntityFormLauncher();
  const accounts = useQuery({
    queryKey: ["bank-accounts", bank?.id],
    enabled: !!bank,
    queryFn: () => listResource<BankAccountRow>(`/banks/accounts?bankId=${bank!.id}`)
  });

  return (
    <Drawer title={`Accounts - ${bank?.name ?? ""}`} open={!!bank} onClose={onClose} width={860}>
      <PageHeaderActions
        title="Bank Accounts"
        right={<Button type="primary" icon={<PlusOutlined />} onClick={() => openForm({ module: "bank-account", mode: "create", initialValues: { bankId: bank?.id }, source: "bank-account-drawer", onSuccess: () => accounts.refetch() })}>Add Account</Button>}
      />
      <PersistedTable
        tableId={`bank-accounts-${bank?.id ?? "none"}`}
        rowKey="id"
        size="small"
        bordered
        loading={accounts.isLoading}
        dataSource={accounts.data ?? []}
        columns={[
          { title: "Account Name", dataIndex: "accountName" },
          { title: "Account No", dataIndex: "accountNumber" },
          {
            title: "Facility",
            width: 180,
            render: (_, row) => (
              <Space direction="vertical" size={0}>
                <Tag color={row.facilityType && row.facilityType !== "Normal" ? "blue" : "default"}>
                  {row.facilityType === "CashCredit" ? "Cash Credit" : row.facilityType ?? "Normal"}
                </Tag>
                {row.facilityType && row.facilityType !== "Normal" ? (
                  <Typography.Text type="secondary">Limit: {row.overdraftLimit ?? 0}</Typography.Text>
                ) : null}
              </Space>
            )
          },
          { title: "Active", dataIndex: "isActive", width: 100, render: (v) => <Tag color={v === false ? "red" : "green"}>{v === false ? "No" : "Yes"}</Tag> },
          {
            title: "",
            width: 52,
            render: (_, row) => (
              <Space size={4}>
                <Button size="small" icon={<EditOutlined />} onClick={() => openForm({ module: "bank-account", mode: "edit", id: row.id, initialValues: row as any, source: "bank-account-drawer", onSuccess: () => accounts.refetch() })} />
              </Space>
            )
          }
        ]}
      />
    </Drawer>
  );
}
